import * as settings from './settings';
import { timeMasks } from 'ngx-mask';

export const DEGREE_CHAR: string = "\xB0";

export const SUP_2_CHAR: string = "\xB2"; // superscript 2 (power of 2)

export const METER_TO_FEET: number = 3.2808;
export const FEET_TO_METER: number = 1.0 / METER_TO_FEET;

export const NM_TO_METER: number = 1852.0;
export const METER_TO_NM: number = 1.0 / NM_TO_METER;

export const KNOT_TO_METER_SEC: number = NM_TO_METER / 3600.0;
export const METER_SEC_TO_KNOT: number = 1.0 / KNOT_TO_METER_SEC;

export const METER_SEC_TO_KMH: number = 3.6;
export const KMH_TO_METER_SEC: number = 1.0 / METER_SEC_TO_KMH;

export const METER_SEC_SQR_TO_KNOT_SEC: number = METER_SEC_TO_KNOT;

export const DEG_TO_RAD: number = Math.PI / 180.0;
export const RAD_TO_DEG: number = 1.0 / DEG_TO_RAD;

export const KM_LABEL = 'km';
export const M_LABEL = 'm';

export const NM_LABEL = 'nm';
export const FT_LABEL = 'ft';

export const HFT_LABEL = 'hft'; // 100ft (for altitude)

export const MS_LABEL = 'm/s'; // m/sec
export const MSSQR_LABEL = `m/s${SUP_2_CHAR}`; // m/sec^2

export const KT_LABEL = 'kt'; // knots
export const KTS_LABEL = 'kt/s'; // knots/sec

export const KMH_LABEL = 'km/h';

export function nextDisplayUnit(unit: settings.DisplayUnits): settings.DisplayUnits {
  unit += 1;
  if (unit > settings.DisplayUnitsTitles.length - 1) {
    unit = 0;
  }
  return unit;
}

export function accelerationUnits(): string {
  switch (settings.userSettings.displayUnits) {
    case settings.DisplayUnits.Metric:
      return MSSQR_LABEL;
    case settings.DisplayUnits.Imperial:
      return KTS_LABEL;
  }
}

export function accelerationValue(accel: number): string {
  switch (settings.userSettings.displayUnits) {
    case settings.DisplayUnits.Metric:
      return accel.toFixed(0);
    case settings.DisplayUnits.Imperial:
      return (accel * METER_SEC_SQR_TO_KNOT_SEC).toFixed(2);
  }
}

export function altitudeUnits(): string {
  switch (settings.userSettings.displayUnits) {
    case settings.DisplayUnits.Metric:
      return KM_LABEL;
    case settings.DisplayUnits.Imperial:
      return FT_LABEL;
  }
}

export function altitudeUnitsVal(unit: settings.DisplayUnits): string {
  switch (unit) {
    case settings.DisplayUnits.Metric:
      return KM_LABEL;
    case settings.DisplayUnits.Imperial:
      return FT_LABEL;
  }
}

export function altitudeValue(alt: number, highPrecision?: boolean): string {
  switch (settings.userSettings.displayUnits) {
    case settings.DisplayUnits.Metric:
      return (alt / 1000.0).toFixed(highPrecision ? 3 : 1);
    case settings.DisplayUnits.Imperial:
      let altFt = alt * METER_TO_FEET;
      let altNeg = altFt < 0;
      let altStr = Math.abs(altFt).toFixed(0);
      return `${altNeg ? '-' : ''}${altStr.padStart(3, '0')}`;
  }
}

// this function gets the desired units and the value to change (in previues format)
// and spits out the new value
export function altitudeValueFix(unit: settings.DisplayUnits, val: number): number {
  switch (unit) {
    case settings.DisplayUnits.Imperial: {
      // if we got here that means the value is metric... and needs to be changed to impirial (ft)
      return val * 1000 * METER_TO_FEET;
    }
    case settings.DisplayUnits.Metric: {
      // if we got here that means the value is imperial... and needs to be changed to metric (km)
      return val * FEET_TO_METER / 1000;
    }
  }
}

export function speedUnits(): string {
  switch (settings.userSettings.displayUnits) {
    case settings.DisplayUnits.Metric:
      return KMH_LABEL;
    case settings.DisplayUnits.Imperial:
      return KT_LABEL;
  }
}

export function speedUnitsVal(unit: settings.DisplayUnits): string {
  switch (unit) {
    case settings.DisplayUnits.Metric:
      return KMH_LABEL;
    case settings.DisplayUnits.Imperial:
      return KT_LABEL;
  }
}

export function speedValue(spd: number, fractionDigits?: number): string {
  switch (settings.userSettings.displayUnits) {
    case settings.DisplayUnits.Metric:
      return (spd * METER_SEC_TO_KMH).toFixed(fractionDigits);
    case settings.DisplayUnits.Imperial:
      return (spd * METER_SEC_TO_KNOT).toFixed(fractionDigits);
  }
}

export function speedValueFix(unit: settings.DisplayUnits, spd: number): number {
  switch (unit) {
    case settings.DisplayUnits.Imperial:
      // if we got here that means the value is metric (m\s)... and needs to be changed to impirial (knots)
      return spd * KMH_TO_METER_SEC * METER_SEC_TO_KNOT;
    case settings.DisplayUnits.Metric:
      // if we got here that means the value is imperial (knots)... and needs to be changed to metric (m\s)
      return spd * KNOT_TO_METER_SEC * METER_SEC_TO_KMH;
  }
}

// export function fixSpdUnits(spd: number, unit: settings.DisplayUnits, fractionDigits?: number): string {
//   switch (settings.userSettings.displayUnits) {
//     case settings.DisplayUnits.Metric:{
//       switch (unit) {
//         case settings.DisplayUnits.Metric:{
//           return spd.toFixed(fractionDigits);
//         }
//         case settings.DisplayUnits.Imperial:{
//           return (spd * METER_SEC_TO_KNOT).toFixed(fractionDigits);
//         }
//       }
//     }
//     case settings.DisplayUnits.Imperial:{
//       switch (unit) {
//         case settings.DisplayUnits.Metric:{
//           return (spd * KNOT_TO_METER_SEC).toFixed(fractionDigits);
//         }
//         case settings.DisplayUnits.Imperial:{
//           return spd.toFixed(fractionDigits);
//         }
//       }
//     }
//   }
// }

export function distanceUnits(): string {
  switch (settings.userSettings.displayUnits) {
    case settings.DisplayUnits.Metric:
      return KM_LABEL;
    case settings.DisplayUnits.Imperial:
      return NM_LABEL;
  }
}

export function distanceValue(dist: number, fractionDigits?: number): string {
  switch (settings.userSettings.displayUnits) {
    case settings.DisplayUnits.Metric:
      return (dist / 1000.0).toFixed(fractionDigits);
    case settings.DisplayUnits.Imperial:
      return (dist * METER_TO_NM).toFixed(fractionDigits);
  }
}

export function headingUnits(): string {
  return DEGREE_CHAR;
}

export function headingValue(heading: number, withUnits?: boolean): string {
  let hdg = Math.round(heading * RAD_TO_DEG);
  if (hdg <= 0)
    hdg += 360;
  else if (hdg > 360)
    hdg -= 360;
  return `${hdg.toString().padStart(3, '0')}${withUnits ? DEGREE_CHAR : ''}`;
}

export function datetime2Str(dtime: Date, ms?: boolean): string {
  return datetime2Date(dtime) + " " + datetime2Time(dtime, ms);
}

export function datetime2StrIso(dtime: Date, ms?: boolean): string {
  return datetime2DateIso(dtime) + "T" + datetime2Time(dtime, ms);
}
// "13/08/23 14:15:41.489"
// "yyyy-MM-ddThh:mm:ss.SSS"

export function datetime2Time(dtime: Date, ms?: boolean): string {
  return dtime.getHours().toString().padStart(2, "0") + ":" +
    dtime.getMinutes().toString().padStart(2, "0") + ":" +
    dtime.getSeconds().toString().padStart(2, "0") +
    (ms ? "." + dtime.getMilliseconds().toString() : "");
}

export function datetime2Date(dtime: Date): string {
  let time = "";
  time = dtime.getUTCDate().toString().padStart(2, "0") + "/" + (dtime.getUTCMonth() + 1).toString().padStart(2, "0") + "/";
  time += dtime.getUTCFullYear().toString().slice(2, 4);
  return time;
}

export function datetime2DateIso(dtime: Date): string {
  let time = "";
  time = dtime.getUTCFullYear().toString() + "-" + (dtime.getUTCMonth() + 1).toString().padStart(2, "0") + "-" + dtime.getUTCDate().toString().padStart(2, "0");
  return time;
}